import WarningIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { percentage } from "../../shared/percentage";
import {
  defendantWinChanceMissing,
  enteredChance,
  winChanceEntered,
} from "../../shared/winChance";
import { NO_VALUE, TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { TonalBadge } from "../general/TonalBadge";
import { YesNoDialog } from "../general/YesNoDialog";
import { IssueCallbacks } from "./IssueEditorRows";
import { IssueKindControl } from "./IssueKindControl";
import { JudgedRow, useJudgedRows } from "./JudgedRow";
import { IssueMenu } from "./IssueMenu";
import {
  Issue,
  Party,
  RowChances,
  involvedDefendantIds,
  winChanceInputOf,
} from "./IssueModel";
import { WinChanceCell } from "./WinChanceCell";
import { WinChanceSlider } from "./WinChanceSlider";
import { GuideMarker } from "../guide/GuideMarker";

export interface IssueDefendant extends Party {
  // defendant's win chance in an issue may be limited (e.g. in XOR issue groups the win chance of all issues must sum to 1)
  maxWinChance?: number;
}

/** The number of an issue or a claim, where its title starts. */
export const OrdinalBadge = ({ children }: { children: ReactNode }) => (
  <TonalBadge
    sx={{
      color: "text.primary",
      minWidth: 24,
      justifyContent: "center",
      px: 0.75,
      ...TABULAR_NUMERALS,
    }}
  >
    {children}
  </TonalBadge>
);

export const IssueRows = ({
  issue,
  ordinal,
  callbacks,
  defendants,
  isFirst = false,
  isLast = false,
  indentRows = 0,
  summaryColumn = false,
  chances,
}: {
  issue: Issue;
  ordinal: string;
  callbacks: IssueCallbacks;
  defendants: IssueDefendant[];
  isFirst?: boolean;
  isLast?: boolean;
  // table rows the empty cell in front of a grouped issue runs down: the
  // issue's own, and the one with its relation to the next issue of the group
  indentRows?: number;
  summaryColumn?: boolean;
  chances: RowChances;
}) => {
  const { t } = useTranslation();
  const [issueToDelete, setIssueToDelete] = useState<Issue | undefined>();
  // who the issue runs against, for the shared markers below
  const involved = involvedDefendantIds(issue);
  const rowInput = winChanceInputOf(issue);

  // the calculation's chances of this issue across its defendants
  const figures = chances.of(issue.id);

  // a single generic chance only replaces something when several defendants
  // are involved; involvement, not an entered chance, is what counts
  const involvedDefendants = defendants.filter(({ id }) =>
    involved.includes(id),
  ).length;
  const enableSwitchToGeneric = issue.generic || involvedDefendants > 1;
  return (
    <>
      <YesNoDialog
        onYes={() => {
          if (issueToDelete !== undefined) callbacks.onDelete(issueToDelete);
          setIssueToDelete(undefined);
        }}
        yesColor="error"
        onNo={() => setIssueToDelete(undefined)}
        open={issueToDelete !== undefined}
        title={t("case.claims.issues.deleteDialog.title")}
        description={t("case.claims.issues.deleteDialog.description", {
          issueOrdinal: ordinal,
        })}
      />
      <TableRow>
        {indentRows > 0 && (
          <TableCell rowSpan={indentRows} sx={{ borderBottom: "none" }} />
        )}
        <TableCell
          colSpan={indentRows ? 1 : 2}
          sx={{ borderBottom: "none", minWidth: 280, maxWidth: 420 }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            sx={{ alignItems: "flex-start" }}
          >
            <OrdinalBadge>{ordinal}</OrdinalBadge>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {issue.title}
              </Typography>
              <JudgedRow issueId={issue.id} />
              {enableSwitchToGeneric && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center", mt: 0.75 }}
                >
                  <IssueKindControl
                    of="issue"
                    generic={issue.generic === true}
                    onSwitchToGeneric={() => callbacks.onSwitchToGeneric(issue)}
                    onSwitchToSpecific={() =>
                      callbacks.onSwitchToSpecific(issue)
                    }
                  />
                  {isFirst && <GuideMarker entry="kind" />}
                </Stack>
              )}
            </Box>
            <IssueMenu
              onEdit={() => callbacks.onIssueEditorRequested(issue)}
              // asked back before the issue goes
              onDelete={() => setIssueToDelete(issue)}
              onMoveUp={isFirst ? undefined : () => callbacks.onMoveUp(issue)}
              onMoveDown={
                isLast ? undefined : () => callbacks.onMoveDown(issue)
              }
            />
          </Stack>
        </TableCell>

        {issue.generic ? (
          <TableCell
            colSpan={defendants.length}
            sx={{
              borderBottom: "none",
            }}
          >
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
              {/* the slider reads 0% whether that was chosen or never entered */}
              {!winChanceEntered(rowInput) && (
                <Tooltip title={t("case.prerequisites.winChanceMissing")} arrow>
                  <WarningIcon fontSize="small" color="warning" />
                </Tooltip>
              )}
              <WinChanceSlider
                onChanceChange={(newChance) =>
                  callbacks.onGenericWinChanceUpdated(issue, newChance)
                }
                chance={issue.genericWinChance ?? 0}
                limit={defendants
                  .map((d) => d.maxWinChance)
                  .filter((value) => value !== undefined)
                  .reduce(
                    (previous, current) =>
                      Math.min(previous ?? 1, current ?? 1),
                    1,
                  )}
              />
            </Stack>
          </TableCell>
        ) : (
          defendants.map((defendant) => (
            <WinChanceCell
              defendant={defendant}
              winChance={enteredChance(issue.winChanceAgainst, defendant.id)}
              involved={involved.includes(defendant.id)}
              isChanceMissing={defendantWinChanceMissing(
                rowInput,
                defendant.id,
              )}
              onWinChanceUpdated={(defendantId: string, newChance: number) =>
                callbacks.onWinChanceUpdated(issue, defendantId, newChance)
              }
              // enforce slider updates when maxWinChance changes (component holds its own position state and will not react otherwise)
              key={"defendant_" + defendant.id + "_" + defendant.maxWinChance}
              limit={defendant.maxWinChance}
            />
          ))
        )}

        {summaryColumn && (
          <IssueWinChanceSummaryCell
            issueOrdinal={ordinal}
            value={figures?.againstAll}
            calculating={chances.calculating}
          />
        )}
        {summaryColumn && !issue.generic && (
          <IssueWinChanceSummaryCell
            issueOrdinal={ordinal}
            vAny
            value={figures?.againstAny}
            calculating={chances.calculating}
          />
        )}
      </TableRow>
    </>
  );
};

/**
 * A win chance over all defendants of a row: against all of them or against
 * any of them, as the column says. Without the number of an issue, the row is
 * the claim as a whole.
 */
export const IssueWinChanceSummaryCell = ({
  issueOrdinal,
  value,
  vAny = false,
  calculating = false,
}: {
  issueOrdinal?: string;
  // the calculation's figure; none until the case can be calculated
  value?: number;
  vAny?: boolean;
  // a change is being calculated: the figure is the one before it
  calculating?: boolean;
}) => {
  // with a judgment there is no chance left to sum up
  const judged = useJudgedRows() !== undefined;

  const { t } = useTranslation(undefined, {
    keyPrefix: "case.claims.issues.summary",
  });
  const column = vAny ? "vAny" : "vAll";

  if (judged) return <TableCell sx={{ borderBottom: "none" }} />;
  return (
    <TableCell
      align="right"
      sx={{
        borderBottom: "none",
      }}
    >
      <Tooltip
        title={
          issueOrdinal === undefined
            ? t(`claimWinChance.tooltip.${column}`)
            : t(`issueWinChance.tooltip.${column}`, { issueOrdinal })
        }
      >
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontWeight: 600,
            opacity: calculating ? 0.5 : 1,
            transition: "opacity 0.2s",
            ...TABULAR_NUMERALS,
          }}
        >
          {value !== undefined ? percentage.format(value) : NO_VALUE}
        </Typography>
      </Tooltip>
    </TableCell>
  );
};
